import type { EconomicBreakdown, ScenarioResult, SimulationInputs } from './model'
import { countryProfiles } from './countryProfiles'

export type MaterialMode = 'gebündelt' | 'teilweise erstattbar' | 'separat erstattbar' | 'nicht erstattbar'
export type DurationMode = 'fallpauschale' | 'tagesabhängig' | 'tarifiert' | 'budgetneutral'

export type CountryAssumptions = {
  settingShiftShare: number
  revenueDeltaPerCase: number
  materialReimbursementShare: number
  durationSensitivity: number
  activityRealisation: number
  capacityRealisation: number
  qualityWeight: number
  implementationFactor: number
  capacityValuePerCase: number
  qualityValuePerQaly: number
  implementationCost: number
  materialMode: MaterialMode
  durationMode: DurationMode
}

export type CountryProfile = {
  id: string
  name: string
  code: string
  currency: string
  asOf: string
  system: string
  settingAndDuration: string
  material: string
  qualityAndContracting: string
  reforms: string[]
  assumptions: CountryAssumptions
  sourceCards: Array<{ title: string; publisher: string; url: string }>
  validationNote: string
}

export type CountryImpact = {
  shiftedCases: number
  reimbursement: number
  materialCoverage: number
  capacityValue: number
  processCost: number
  qualityValue: number
  netImpact: number
  qaly: number
  dominantLever: string
  rows: Array<{ key: string; label: string; value: number; description: string }>
}

export { countryProfiles }

export function calculateCountryImpact(
  profile: CountryProfile,
  assumptions: CountryAssumptions,
  inputs: SimulationInputs,
  scenario: ScenarioResult,
  economics: EconomicBreakdown,
): CountryImpact {
  const relevantCases = Math.max(0, inputs.annualCases * inputs.inclusionShare)
  const shiftedCases = relevantCases * assumptions.settingShiftShare
  const reimbursement = shiftedCases * assumptions.revenueDeltaPerCase * assumptions.activityRealisation * assumptions.durationSensitivity
  const materialVerified = assumptions.materialMode === 'teilweise erstattbar' || assumptions.materialMode === 'separat erstattbar'
  const materialCoverage = materialVerified ? scenario.deviceCost * assumptions.materialReimbursementShare : 0
  const capacityValue = scenario.additionalCases * assumptions.capacityValuePerCase * assumptions.capacityRealisation
  const processCost = -assumptions.implementationCost * assumptions.implementationFactor
  const qualityValue = scenario.qaly * assumptions.qualityValuePerQaly * assumptions.qualityWeight
  const netImpact = reimbursement + materialCoverage + capacityValue + processCost + qualityValue
  const rows = [
    { key: 'reimbursement', label: 'Nettoerlös aus Setting-/Tarifwechsel', value: reimbursement, description: 'Wechselanteil × Erlösdifferenz/Fall × Aktivitäts- und Verweildauerwirkung.' },
    { key: 'material', label: 'Materialkostendeckung', value: materialCoverage, description: materialVerified ? 'Anteil der erstatteten Systemkosten im explizit geprüften Erstattungsmodus.' : 'Standardwert gebündelt: Keine separate Materialerstattung ohne positiven Listen-, Code- oder Vertragsnachweis.' },
    { key: 'capacity', label: 'Marginaler Kapazitätswert', value: capacityValue, description: 'Zusätzliche Fälle × lokaler Wert/Fall × realisierbarer Kapazitätsanteil.' },
    { key: 'process', label: 'Implementierungs- und Prozesskosten', value: processCost, description: 'Lokale Einführungskosten × Implementierungsfaktor.' },
    { key: 'quality', label: 'Monetarisierter Qualitätseffekt', value: qualityValue, description: 'QALY-Effekt × lokaler Bewertungswert × Qualitätsgewichtung.' },
  ]
  const dominant = rows.reduce((previous, current) => Math.abs(current.value) > Math.abs(previous.value) ? current : previous, rows[0])

  return {
    shiftedCases,
    reimbursement,
    materialCoverage,
    capacityValue,
    processCost,
    qualityValue,
    netImpact,
    qaly: scenario.qaly,
    dominantLever: dominant.label,
    rows,
  }
}

export function countryAssumptionLabel(key: keyof CountryAssumptions) {
  const labels: Record<keyof CountryAssumptions, string> = {
    settingShiftShare: 'Wechselanteil in Zielpfad',
    revenueDeltaPerCase: 'Nettoerlösdifferenz je Wechsel-Fall',
    materialReimbursementShare: 'Erstattungsanteil Sachkosten',
    durationSensitivity: 'Verweildauer-Sensitivität',
    activityRealisation: 'Aktivitäts-/Vertragsrealisierung',
    capacityRealisation: 'Realisierung freier Kapazität',
    qualityWeight: 'Gewichtung Qualität/P4P',
    implementationFactor: 'Implementierungsfaktor',
    capacityValuePerCase: 'Marginaler Kapazitätswert je Fall',
    qualityValuePerQaly: 'Lokaler Wert je QALY',
    implementationCost: 'Einmalige Implementierungskosten',
    materialMode: 'Sachkostenerstattungsmodus',
    durationMode: 'Verweildauermechanik',
  }
  return labels[key]
}
